import React from 'react'
import { TextInputProps } from './types'

function TextInput({
  value,
  onChange,
  type = 'text',
  placeholder,
  disabled = false,
  readOnly = false,
  required = false,
  className = '',
  name,
  id,
  maxLength,
  minLength,
  size = 'md',
  icon,
  iconPosition = 'left',
  ...props
}: TextInputProps): JSX.Element {

  // Base classes for normal state
  const baseClasses = 'tw-w-full tw-rounded-lg tw-border focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-border-blue-500'
  
  // Conditional classes based on state
  const stateClasses = readOnly 
    ? 'tw-border-gray-300 dark:tw-border-[#364152] tw-bg-gray-50 dark:tw-bg-gray-800 tw-text-gray-700 dark:tw-text-gray-300 tw-cursor-not-allowed placeholder:tw-text-gray-400 dark:placeholder:tw-text-gray-500'
    : disabled
      ? 'tw-border-gray-300 dark:tw-border-gray-600 tw-bg-gray-50 dark:tw-bg-gray-800 tw-text-gray-500 tw-cursor-not-allowed placeholder:tw-text-gray-500'
      : 'tw-border-gray-300 dark:tw-border-gray-600 tw-bg-white dark:tw-bg-gray-800 tw-text-gray-900 dark:tw-text-white placeholder:tw-text-gray-500'
  
  const sizeClasses = {
    sm: 'tw-px-4 tw-py-2 tw-text-sm',
    md: 'tw-px-4 tw-py-2.5 tw-text-sm',
    lg: 'tw-px-4 tw-py-3 tw-min-h-[44px] tw-text-base'
  }
  
  const iconClasses = icon ? (iconPosition === 'left' ? 'tw-pl-10' : 'tw-pr-10') : ''
  
  const inputClasses = `${baseClasses} ${stateClasses} ${sizeClasses[size]} ${iconClasses} ${className}`

  // Only wrap in relative div if we have an icon
  if (icon) {
    return (
      <div className="tw-relative">
        <div className={`tw-absolute tw-inset-y-0 ${iconPosition === 'left' ? 'tw-left-0' : 'tw-right-0'} tw-flex tw-items-center tw-px-3`}>
          <span className="material-symbols-outlined tw-text-gray-400 tw-text-base">
            {icon}
          </span>
        </div>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          className={inputClasses}
          name={name}
          id={id}
          maxLength={maxLength}
          minLength={minLength}
          {...props}
        />
      </div>
    )
  }

  // No wrapper div when no icon - allows proper flex behavior
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      required={required}
      className={inputClasses}
      name={name}
      id={id}
      maxLength={maxLength}
      minLength={minLength}
      {...props}
    />
  )
}

export default TextInput